import { getAuthorizationActions, getAuthorizationGroups } from "@/Common/api/authorization/queries";
import { IPageParam } from "@/Common/api/types/pagination";
import { useBasicPaginatedResource } from "@/Common/hooks/Pagination/useBasicPaginatedResource";
import { getUsersByLocationPaginated } from "@/DataPages/api/users/queries";
import { useQuery } from "@tanstack/react-query";
import uniqBy from "lodash-es/uniqBy";
import { IGetProperQueriesArgs, IGroupUserExtended } from "../interfaces";

const PAGE_SIZE = 100;

const getProperQueries = (args: IGetProperQueriesArgs) => {
	const { locationId, scope, type } = args ?? {};

	switch (type) {
		case "groups":
			return {
				queryKey: ["authorizationGroups", locationId],
				queryFn: () => getAuthorizationGroups(locationId),
				isExecutable: !!locationId,
			};
		case "actions":
			return {
				queryKey: ["authorizationActions", locationId],
				queryFn: () => getAuthorizationActions(locationId, [scope]),
				isExecutable: !!scope,
			};
		default:
			return { queryKey: ["noAccessList"], queryFn: () => {}, isExecutable: false };
	}
};

export const useAccessListData = (args: IGetProperQueriesArgs) => {
	const { queryKey, queryFn, isExecutable } = getProperQueries(args);
	const { data: queryResult, ...restQuery } = useQuery({
		queryKey,
		queryFn,
		enabled: isExecutable,
	});

	return { data: queryResult, ...restQuery, hasNextPage: false };
};

export const useAccessListUsers = (locationId: string, userType: string, preloadedData: IGroupUserExtended[]) => {
	const { data: paginatedData, ...otherResults } = useBasicPaginatedResource({
		queryKey: ["authorizationLocalUsers", locationId, userType],
		queryFn: async ({ pageParam }) => {
			const typedPageParam = pageParam as IPageParam<string>;
			if (!typedPageParam || !("after" in typedPageParam) || !("first" in typedPageParam)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}

			const users = await getUsersByLocationPaginated(locationId, userType, {
				first: typedPageParam.first,
				after: typedPageParam.after,
			});

			if (!users) {
				return {
					items: [],
					pageInfo: {
						hasNextPage: false,
						endCursor: "",
					},
				};
			}
			return users;
		},
		getNextPageParam: (lastPage) => {
			const { pageInfo } = lastPage ?? {};
			return pageInfo?.hasNextPage ? { first: PAGE_SIZE, after: pageInfo.endCursor ?? undefined } : undefined;
		},
		initialPageParam: {
			first: PAGE_SIZE,
			after: "",
		},
		fetchAllPages: true,
		enabled: Boolean(locationId),
		staleTime: Infinity,
	});

	const dataP = combinePreloadedAndPaginatedData(locationId, paginatedData ?? [], userType, preloadedData);
	return {
		data: dataP,
		...otherResults,
	};
};

const combinePreloadedAndPaginatedData = (
	currentLocation: string,
	paginatedData: IGroupUserExtended[],
	userType: string,
	preloadedData?: IGroupUserExtended[]
): IGroupUserExtended[] => {
	const paginatedUsers = paginatedData.flatMap((user) => {
		return {
			...user,
			icon: user.locationId === currentLocation ? "location" : "",
		};
	});

	if (!preloadedData?.length) {
		return paginatedUsers;
	}

	const disabledPreloadedData = preloadedData
		.filter((user) => {
			if (userType === "local") {
				return user.locationId === currentLocation;
			}
			return user.locationId !== currentLocation;
		})
		.map((user) => ({
			...user,
			forceDisabled: currentLocation !== user.locationId,
			icon: user.locationId === currentLocation ? "location" : "",
		}));

	return uniqBy([...paginatedUsers, ...disabledPreloadedData], "_id");
};
